/* Support Hub — pure presentation + badge helpers (marker: support-hub-modal).
 *
 * Backs the topbar Support & feedback surface (src/shared/SupportHub.tsx +
 * src/shared/SupportHubModal.tsx). Framework-agnostic and unit-tested
 * (docs/REGRESSIONS.md) because two paths are trust-boundary/UX-critical:
 *
 *   1. The unseen-answers BADGE is driven by a localStorage snapshot
 *      ("which of my submissions had an answer the last time I opened the
 *      hub"). localStorage contents are untrusted junk after upgrades /
 *      manual edits / quota failures — parsing must never throw, and the
 *      badge must never wedge itself permanently on (a stale key can only
 *      ever HIDE a dot, not stick one). Server rows are equally untrusted
 *      shapes (missing ids, null solutions, unknown kinds).
 *
 *   2. Status chips shown to END USERS must map internal pipeline states
 *      (error_tickets.upstream_status / feature_requests.status) to honest
 *      but humane labels — "failed to forward upstream" is an internal
 *      retry state, not something to alarm the reporting user with. Chip
 *      lookups use Object.hasOwn (never `in` — prototype-chain junk like
 *      "constructor" must fall through to the safe default).
 */

// ── Submission shape (what the badge/chip logic needs) ─────────────────

export type SupportSubmissionKind = 'issue' | 'feature'

/** Minimal slice of GET /api/support/mine rows the pure logic reads. */
export type SupportSubmissionLite = {
  kind?: unknown
  id?: unknown
  solution?: unknown
}

// ── Unseen-answer badge (localStorage last-seen snapshot) ───────────────

export const SUPPORT_SEEN_STORAGE_KEY = 'support-hub-answers-seen-v1'
/** Hard cap on persisted keys — the /mine endpoint caps at 50 rows, so 300
 *  covers months of churn without letting the entry grow unbounded. */
export const MAX_SEEN_KEYS = 300

/** Stable identity for one submission across both kinds: "issue:12". */
export function submissionKey(kind: unknown, id: unknown): string {
  const k = typeof kind === 'string' && kind.trim() ? kind.trim().toLowerCase() : 'unknown'
  const i = typeof id === 'number' || typeof id === 'string' ? String(id) : ''
  return `${k}:${i}`
}

/** A submission "has an answer" when `solution` is a non-empty string.
 *  (Status alone is not enough — solved rows always carry the text, and a
 *  status-only transition with nothing to read shouldn't badge.) */
export function hasAnswer(sub: SupportSubmissionLite | null | undefined): boolean {
  if (!sub || typeof sub !== 'object') return false
  return typeof sub.solution === 'string' && sub.solution.trim().length > 0
}

/** Keys of the submissions that currently have an answer. Junk entries
 *  (missing id, non-object rows) are skipped, never thrown on. */
export function answeredKeys(subs: ReadonlyArray<SupportSubmissionLite> | null | undefined): string[] {
  if (!Array.isArray(subs)) return []
  const out: string[] = []
  for (const sub of subs) {
    if (!hasAnswer(sub)) continue
    const key = submissionKey(sub!.kind, sub!.id)
    if (key.endsWith(':')) continue // no usable id — can't track seen-ness
    out.push(key)
  }
  return out
}

/** Defensive parse of the stored seen-keys JSON. Malformed JSON, non-arrays,
 *  and non-string entries degrade to [] / get dropped; never throws. */
export function parseSeenKeys(raw: unknown): string[] {
  if (typeof raw !== 'string' || !raw.trim()) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  const out: string[] = []
  for (const entry of parsed) {
    if (typeof entry === 'string' && entry.length > 0 && entry.length <= 64) out.push(entry)
    if (out.length >= MAX_SEEN_KEYS) break
  }
  return out
}

/** Merge newly-seen keys into the previous set and serialize for storage.
 *  Dedupes; keeps the LAST MAX_SEEN_KEYS (newest wins — old keys age out). */
export function serializeSeenKeys(prev: ReadonlyArray<string>, newlySeen: ReadonlyArray<string>): string {
  const merged: string[] = []
  const seen = new Set<string>()
  for (const key of [...(Array.isArray(prev) ? prev : []), ...(Array.isArray(newlySeen) ? newlySeen : [])]) {
    if (typeof key !== 'string' || !key) continue
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(key)
  }
  // Newest arrivals sit at the tail, so the tail is what survives the cap.
  return JSON.stringify(merged.slice(-MAX_SEEN_KEYS))
}

/** How many of my submissions gained an answer I haven't opened the hub to
 *  see. Stale seen-keys (submissions that fell off the list) are harmless —
 *  they can only hide, never show, a badge. */
export function countUnseenAnswers(
  subs: ReadonlyArray<SupportSubmissionLite> | null | undefined,
  seenKeys: ReadonlyArray<string> | null | undefined,
): number {
  const seen = new Set(Array.isArray(seenKeys) ? seenKeys : [])
  let count = 0
  for (const key of answeredKeys(subs)) {
    if (!seen.has(key)) count += 1
  }
  return count
}

// ── User-facing status chips ────────────────────────────────────────────
// Same visual contract as the Error Center's TicketStatusChip (bg/fg/border/
// dot hexes from the same palette) so chips read identically across both
// surfaces — but with END-USER labels: internal pipeline states must not
// leak ("failed" = upstream forward failed; the cron retries + admins see
// it in the Error Center — the reporting user just needs "the team has it").

export type SupportStatusChip = {
  status: string
  label: string
  bg: string
  fg: string
  border: string
  dot: string
}

const CHIP_GREEN: Omit<SupportStatusChip, 'status' | 'label'> = { bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0', dot: '#22c55e' }
const CHIP_AMBER: Omit<SupportStatusChip, 'status' | 'label'> = { bg: '#fffbeb', fg: '#b45309', border: '#fde68a', dot: '#f59e0b' }
const CHIP_SLATE: Omit<SupportStatusChip, 'status' | 'label'> = { bg: '#f8fafc', fg: '#475569', border: '#e2e8f0', dot: '#94a3b8' }
const CHIP_BLUE: Omit<SupportStatusChip, 'status' | 'label'> = { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6' }

/** error_tickets.upstream_status → end-user chip. */
const ISSUE_CHIPS: Record<string, SupportStatusChip> = {
  solved: { status: 'solved', label: 'Answered', ...CHIP_GREEN },
  pending: { status: 'pending', label: 'In review', ...CHIP_SLATE },
  needs_human: { status: 'needs_human', label: 'With the team', ...CHIP_AMBER },
  failed: { status: 'failed', label: 'With the team', ...CHIP_AMBER },
  disabled: { status: 'disabled', label: 'Received', ...CHIP_SLATE },
}

/** feature_requests.status → end-user chip. */
const FEATURE_CHIPS: Record<string, SupportStatusChip> = {
  submitted: { status: 'submitted', label: 'Submitted', ...CHIP_SLATE },
  forwarded: { status: 'forwarded', label: 'Sent to Bot Pros', ...CHIP_BLUE },
  acknowledged: { status: 'acknowledged', label: 'Acknowledged', ...CHIP_GREEN },
  failed: { status: 'failed', label: 'With the team', ...CHIP_AMBER },
}

const FALLBACK_CHIP: SupportStatusChip = { status: 'unknown', label: 'Received', ...CHIP_SLATE }

function chipFrom(table: Record<string, SupportStatusChip>, status: string | null | undefined): SupportStatusChip {
  if (typeof status === 'string') {
    const key = status.trim().toLowerCase()
    // Object.hasOwn (not `in`) — `in` walks the prototype chain, so junk
    // like "constructor" would return a non-chip object.
    if (Object.hasOwn(table, key)) return table[key]
  }
  return FALLBACK_CHIP
}

/** Chip for a submission row. Issue rows use error_tickets statuses;
 *  feature rows use feature_requests statuses; anything else falls back
 *  to the neutral "Received" chip. */
export function supportStatusChip(kind: unknown, status: string | null | undefined): SupportStatusChip {
  const k = typeof kind === 'string' ? kind.trim().toLowerCase() : ''
  if (k === 'feature') return chipFrom(FEATURE_CHIPS, status)
  return chipFrom(ISSUE_CHIPS, status)
}

// ── Kind chips ──────────────────────────────────────────────────────────

export type SupportKindChip = { label: string; bg: string; fg: string; border: string }

/** "Issue" (slate-blue) vs "Wish" (violet) tag next to each submission. */
export function supportKindChip(kind: unknown): SupportKindChip {
  const k = typeof kind === 'string' ? kind.trim().toLowerCase() : ''
  if (k === 'feature') return { label: 'Wish', bg: '#f5f3ff', fg: '#6d28d9', border: '#ddd6fe' }
  return { label: 'Issue', bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' }
}
